import React, { useEffect, useState } from "react";
import { CalendarDays, RefreshCw, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, inr } from "@/lib/api";

const statusLabel = (value) => String(value || "pending").replace(/_/g, " ");

export const ReturnsTab = () => {
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState("");
  const [pickupDates, setPickupDates] = useState({});

  const load = async () => {
    const response = await api.get("/admin/returns");
    setRequests(response.data);
  };

  useEffect(() => {
    load().catch((error) => toast.error(formatError(error)));
  }, []);

  const updateStatus = async (request, status) => {
    setBusy(`${request.id}-${status}`);
    try {
      await api.put(`/admin/returns/${request.id}/status`, { status });
      toast.success(`Return ${status}`);
      await load();
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setBusy("");
    }
  };

  const schedulePickup = async (request) => {
    setBusy(`${request.id}-schedule`);
    try {
      const response = await api.post(`/admin/returns/${request.id}/reverse-pickup`, {
        pickup_date: pickupDates[request.id] || null,
      });
      setRequests((current) => current.map((item) => (
        item.id === request.id
          ? { ...item, reverse_pickup: response.data.reverse_pickup }
          : item
      )));
      toast.success("Reverse pickup scheduled");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setBusy("");
    }
  };

  const refreshPickup = async (request) => {
    setBusy(`${request.id}-refresh`);
    try {
      const response = await api.post(`/admin/returns/${request.id}/reverse-pickup/refresh`);
      setRequests((current) => current.map((item) => (
        item.id === request.id
          ? { ...item, reverse_pickup: response.data.reverse_pickup }
          : item
      )));
      toast.success("Reverse pickup status refreshed");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setBusy("");
    }
  };

  return (
    <section data-testid="admin-returns-tab">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8C6D46]">
            Customer care
          </p>
          <h2 className="mt-2 font-serif text-3xl font-semibold">Returns</h2>
        </div>
        <button
          type="button"
          onClick={() => load().catch((error) => toast.error(formatError(error)))}
          className={[
            "inline-flex items-center gap-2 border border-[#2D5A47] px-3 py-2",
            "text-sm font-semibold text-[#2D5A47] hover:bg-[#EBF2EE]",
          ].join(" ")}
          data-testid="admin-returns-refresh"
        >
          <RefreshCw size={15} /> Refresh
        </button>
      </div>
      {!requests.length ? (
        <div className="border border-[#E2DCD2] bg-white p-8 text-center text-sm text-[#64748B]">
          No return requests yet.
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => {
            const pickup = request.reverse_pickup;
            const scheduled = pickup?.state === "scheduled";
            const pickupErrorText = pickup?.last_error
              ? typeof pickup.last_error === "string"
                ? pickup.last_error
                : JSON.stringify(pickup.last_error)
              : "";
            return (
              <article
                key={request.id}
                className="border border-[#E2DCD2] bg-white p-5"
                data-testid={`admin-return-${request.id}`}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <p className="font-mono text-xs text-[#64748B]">
                      Order #{request.order_id.slice(0, 8)}
                    </p>
                    <h3 className="mt-1 font-semibold text-[#1E2A24]">
                      {request.customer?.name || "Customer"}
                    </h3>
                    <p className="mt-1 text-sm text-[#64748B]">{request.reason}</p>
                    <p className="mt-3 text-xs text-[#64748B]">
                      {(request.items || []).map((item) => (
                        `${item.quantity} × ${item.name} · ${inr(item.price * item.quantity)}`
                      )).join(" · ")}
                    </p>
                  </div>
                  <span
                    className={[
                      "w-fit border border-[#D9C3A5] bg-[#FDFBF7] px-2.5 py-1",
                      "text-xs font-semibold capitalize text-[#8C6D46]",
                    ].join(" ")}
                    data-testid={`admin-return-status-${request.id}`}
                  >
                    {statusLabel(request.status)}
                  </span>
                </div>
                {request.status === "requested" && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => updateStatus(request, "approved")}
                      disabled={busy !== ""}
                      className={[
                        "bg-[#2D5A47] px-4 py-2 text-sm font-semibold text-white",
                        "disabled:opacity-50",
                      ].join(" ")}
                      data-testid={`approve-return-${request.id}`}
                    >
                      {busy === `${request.id}-approved` ? "Approving…" : "Approve return"}
                    </button>
                    <button
                      type="button"
                      onClick={() => updateStatus(request, "rejected")}
                      disabled={busy !== ""}
                      className={[
                        "border border-[#A63A2B] px-4 py-2 text-sm font-semibold",
                        "text-[#A63A2B] disabled:opacity-50",
                      ].join(" ")}
                      data-testid={`reject-return-${request.id}`}
                    >
                      Reject
                    </button>
                  </div>
                )}
                {request.status === "approved" && (
                  <div className="mt-5 border-t border-[#E2DCD2] pt-4">
                    {pickup ? (
                      <div
                        className="flex flex-wrap items-center justify-between gap-3 text-sm"
                        data-testid={`reverse-pickup-status-${request.id}`}
                      >
                        <div>
                          <span className="font-semibold text-[#2D5A47]">
                            Reverse pickup: {statusLabel(pickup.status || pickup.state)}
                          </span>
                          {pickup.awb && (
                            <span className="ml-2 font-mono text-xs text-[#64748B]">
                              AWB {pickup.awb}
                            </span>
                          )}
                          {pickup.courier && (
                            <span className="ml-2 text-xs text-[#64748B]">{pickup.courier}</span>
                          )}
                          {pickup.warehouse_location && (
                            <p className="mt-1 text-xs text-[#64748B]">
                              Return destination: {pickup.warehouse_location}
                            </p>
                          )}
                          {pickupErrorText && (
                            <p
                              className="mt-1 text-xs font-semibold text-red-700"
                              data-testid={`reverse-pickup-error-${request.id}`}
                            >
                              Reverse pickup error: {pickupErrorText}
                            </p>
                          )}
                        </div>
                        {scheduled && (
                          <button
                            type="button"
                            onClick={() => refreshPickup(request)}
                            disabled={busy !== ""}
                            className={[
                              "inline-flex items-center gap-1 text-xs font-semibold text-[#2D5A47]",
                              "hover:underline",
                            ].join(" ")}
                            data-testid={`refresh-reverse-pickup-${request.id}`}
                          >
                            <RefreshCw size={13} /> Refresh status
                          </button>
                        )}
                        {pickup.state === "failed" && (
                          <button
                            type="button"
                            onClick={() => schedulePickup(request)}
                            disabled={busy !== ""}
                            className={[
                              "inline-flex items-center gap-1 text-xs font-semibold text-red-700",
                              "hover:underline disabled:opacity-50",
                            ].join(" ")}
                            data-testid={`retry-reverse-pickup-${request.id}`}
                          >
                            <RefreshCw size={13} />
                            {busy === `${request.id}-schedule`
                              ? "Retrying…"
                              : "Retry reverse pickup"}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-end gap-3">
                        <label className="text-xs font-semibold text-[#64748B]">
                          Pickup date (optional)
                          <input
                            type="date"
                            value={pickupDates[request.id] || ""}
                            onChange={(event) => setPickupDates({
                              ...pickupDates,
                              [request.id]: event.target.value,
                            })}
                            className={[
                              "mt-1 block border border-[#E2DCD2] px-3 py-2 text-sm",
                              "text-[#1E2A24]",
                            ].join(" ")}
                            data-testid={`reverse-pickup-date-${request.id}`}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => schedulePickup(request)}
                          disabled={busy !== ""}
                          className={[
                            "inline-flex items-center gap-2 bg-[#C86D42] px-4 py-2",
                            "text-sm font-semibold text-white disabled:opacity-50",
                          ].join(" ")}
                          data-testid={`schedule-reverse-pickup-${request.id}`}
                        >
                          <Truck size={16} />
                          {busy === `${request.id}-schedule`
                            ? "Scheduling…"
                            : "Schedule Reverse Pickup"}
                        </button>
                        <p className="self-center text-xs text-[#64748B]">
                          <CalendarDays size={13} className="mr-1 inline" />
                          Leave blank for Shiprocket&apos;s earliest available date.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};